<template>
    <div
        ref="rootEl"
        class="modal-container modal-container-corner no-transition"
        :class="{ hidden: !display }"
        tabindex="-1"
        role="dialog"
        :aria-hidden="!display"
        @click="close"
        @keydown="escapeToClose"
    >
        <div class="modal-dialog modal-md account-settings-dd" role="document" @click="stopPropagationEvent">
            <header class="account-settings-dd-header">
                <figure class="account-settings-dd-avatar">
                    <img v-if="profileImage" class="usr-image" :src="profileImage" :alt="profileName || username" />
                    <img v-else class="usr-image" src="@/assets/user.png" :alt="profileName || username" />
                </figure>
                <div class="account-settings-dd-identity">
                    <h3 class="account-settings-dd-name">{{ profileName || username }}</h3>
                    <p v-if="profileName" class="account-settings-dd-username">@{{ username }}</p>
                </div>
            </header>

            <div class="account-settings-dd-body">
                <div class="account-settings-dd-preferences">
                    <div class="settings-dd-row">
                        <div class="settings-dd-row-label">
                            <span class="settings-dd-icon" aria-hidden="true">
                                <i class="fas fa-moon"></i>
                            </span>
                            <span>{{ t("Dark mode") }}</span>
                        </div>
                        <ToggleSwitch v-model:val="isDarkTheme" compact @update:val="onThemeChanged"></ToggleSwitch>
                    </div>

                    <div class="settings-dd-row">
                        <div class="settings-dd-row-label">
                            <span class="settings-dd-icon" aria-hidden="true">
                                <i class="fas fa-globe"></i>
                            </span>
                            <span>{{ t("Language") }}</span>
                        </div>
                        <UiSelect
                            v-model:val="lang"
                            :options="languageOptions"
                            compact
                            class="settings-dd-select"
                            :aria-label="t('Language')"
                            @change="onLanguageChanged"
                            @click.stop
                        ></UiSelect>
                    </div>
                </div>

                <div class="account-settings-dd-actions">
                    <button v-if="canAdmin" type="button" class="settings-dd-link" @click="goToAdmin">
                        <span class="settings-dd-icon" aria-hidden="true">
                            <i class="fas fa-hammer"></i>
                        </span>
                        <span>{{ t("Administration and moderation") }}</span>
                    </button>

                    <button type="button" class="settings-dd-link settings-dd-link--danger" @click="logout">
                        <span class="settings-dd-icon settings-dd-icon--danger" aria-hidden="true">
                            <i class="fas fa-right-from-bracket"></i>
                        </span>
                        <span>{{ t("Log out") }}</span>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, useTemplateRef } from "vue";
import { useRouter } from "vue-router";
import { AuthController } from "@/control/auth";
import { AVAILABLE_LANGUAGES, getLanguage, setLanguage } from "@/i18n";
import { getTheme, setTheme, type ColorThemeName } from "@/control/app-preferences";
import ToggleSwitch from "@/components/utils/ToggleSwitch.vue";
import UiSelect from "@/components/utils/UiSelect.vue";
import { useDropdownPanel } from "@/composables/useDropdownPanel";

defineOptions({ name: "AccountSettingsDropdown" });

const REQUIRED_ADMIN_PERMISSIONS = ["mod.users", "admin.roles", "admin.monitoring"];

const display = defineModel<boolean>("display");
const emit = defineEmits<{
    openModal: [name: string];
}>();

const rootEl = useTemplateRef<HTMLElement>("rootEl");
const router = useRouter();

const profileName = ref(AuthController.ProfileName);
const profileImage = ref(AuthController.ProfileImage);
const username = ref(AuthController.Username);
const languageOptions = AVAILABLE_LANGUAGES.map((l) => {
    return { value: l.id, label: l.name };
});
const lang = ref(getLanguage());
const isDarkTheme = ref(getTheme() === "dark");
const canAdmin = ref(REQUIRED_ADMIN_PERMISSIONS.filter((p) => AuthController.hasPermission(p)).length > 0);

const onAuthChanged = () => {
    username.value = AuthController.Username;
    profileImage.value = AuthController.ProfileImage;
    profileName.value = AuthController.ProfileName;
    canAdmin.value = REQUIRED_ADMIN_PERMISSIONS.filter((p) => AuthController.hasPermission(p)).length > 0;
};

const { close, escapeToClose, stopPropagationEvent } = useDropdownPanel(display, rootEl, {
    focusTrapExceptClass: "top-bar-settings-button",
    onActivate: () => {
        onAuthChanged();
        lang.value = getLanguage();
        isDarkTheme.value = getTheme() === "dark";
    },
});

const logout = () => {
    emit("openModal", "logout");
};

const goToAdmin = () => {
    close();
    router.push({ name: "admin" });
};

const onThemeChanged = (dark: boolean) => {
    const theme: ColorThemeName = dark ? "dark" : "light";
    setTheme(theme);
};

const onLanguageChanged = () => {
    setLanguage(lang.value);
};
</script>
